
import React from 'react';
import { Row, Col, Card, Badge, Button, UncontrolledTooltip } from 'reactstrap';
import moment from 'moment';
import 'moment/locale/es';
import "../assets/scss/SeriesGrid.scss";

// Assets
import btdiggIcon from "../assets/img/btdigg.png";
import googleIcon from "../assets/img/google.jpg";

// Helper Component for the Card Image
// Fetches a vertical poster from TMDB using the series name
const SeriesCardImage = ({ serie }) => {
    const [posterUrl, setPosterUrl] = React.useState(null);
    const [loading, setLoading] = React.useState(true);
    
    // TMDB API Key from Environment or Fallback to the one found in backend
    const TMDB_API_KEY = process.env.REACT_APP_TMDB_API_KEY || "f090bb54758cabf231fb605d3e3e0468"; 
    
    React.useEffect(() => {
        let isMounted = true;
        const fetchPoster = async () => {
            const cacheKey = `poster_cache_v2_${serie._id}`; 
            const cached = localStorage.getItem(cacheKey);

            // 1. Priority: If the database already has an image, use it!
            // We only search if the database has the default placeholder or is empty.
            const hasRealImage = serie.image && serie.image !== "" && !serie.image.includes('default.png');
            
            if (hasRealImage) {
                if (isMounted) {
                    setPosterUrl(serie.image);
                    setLoading(false);
                    // Update cache to match the reality in DB
                    if (cached !== serie.image) {
                        localStorage.setItem(cacheKey, serie.image);
                    }
                }
                return;
            }

            // 2. If nothing in DB but we have cache, use cache
            if (cached) {
                if (isMounted) {
                    setPosterUrl(cached);
                    setLoading(false);
                }
                return;
            }

            // 3. Last resort: Search TMDB
            try {
                const query = encodeURIComponent(serie.nom);
                const res = await fetch(`https://api.themoviedb.org/3/search/tv?api_key=${TMDB_API_KEY}&query=${query}&language=es-ES&page=1&include_adult=false`);
                
                if (!res.ok) throw new Error("TMDB Error");
                
                const data = await res.json();
                
                if (isMounted) {
                    if (data.results && data.results.length > 0) {
                         // Strictly find a POSTER
                         const withPoster = data.results.find(s => s.poster_path);
                         const bestMatch = withPoster || data.results[0];
                         
                         if (bestMatch.poster_path) {
                             const url = `https://image.tmdb.org/t/p/w500${bestMatch.poster_path}`;
                             setPosterUrl(url);
                             localStorage.setItem(cacheKey, url);
                         } else {
                             setPosterUrl(serie.image);
                         }
                    } else {
                        setPosterUrl(serie.image);
                    }
                    setLoading(false);
                }
            
            } catch (e) {
                console.error("Poster fetch error:", e);
                if (isMounted) {
                    setPosterUrl(serie.image);
                    setLoading(false);
                }
            }
        };

        if (serie.nom) fetchPoster();
        else { setPosterUrl(serie.image); setLoading(false); }
        
        return () => { isMounted = false; };

    }, [serie.nom, serie.image, serie._id, TMDB_API_KEY]);

    return (
        <img 
            src={posterUrl || serie.image || "https://via.placeholder.com/300x450?text=No+Poster"} 
            alt={serie.nom}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }} // Vertical posters should always cover to fill the vertical slot
            onError={(e) => { 
                e.target.onerror = null; 
                // Prevent infinite loop if fallback also fails
                if (e.target.src !== serie.image && serie.image) {
                     e.target.src = serie.image; 
                } else {
                     e.target.src="https://via.placeholder.com/300x450?text=No+Poster";
                }
            }}
        />
    );
};

const SeriesGrid = ({
    series,
    onEdit,
    onDelete,
    onActivate,
    onDeactivate,
    onIncrement,
    onAutoDownload, // <-- Nueva prop
}) => {

    if (!series || series.length === 0) {
        return (
            <div className="text-center py-5">
                <i className="bi bi-collection display-1 text-muted opacity-25 mb-3"></i>
                <h4 className="text-muted fw-light">No tens sèries en aquest llistat.</h4>
                <p className="text-muted small">Afegeix-ne una des de "Novedades" o el botó "+"</p>
            </div>
        );
    }

    return (
        <Row>
            {series.map((serie) => {
                 // Función para obtener el color del badge según el género
                 const getGenreColor = (genero) => {
                     const colorMap = {
                         "Drama": { bg: "#e74c3c", color: "#fff" }, // Rojo
                         "Comèdia": { bg: "#f39c12", color: "#fff" }, // Naranja
                         "Acció": { bg: "#e67e22", color: "#fff" }, // Naranja oscuro
                         "Ciència-ficció": { bg: "#9b59b6", color: "#fff" }, // Púrpura
                         "Terror": { bg: "#34495e", color: "#fff" }, // Gris oscuro
                         "Thriller": { bg: "#c0392b", color: "#fff" }, // Rojo oscuro
                         "Fantasia": { bg: "#8e44ad", color: "#fff" }, // Morado
                         "Documental": { bg: "#16a085", color: "#fff" }, // Verde azulado
                         "Animació": { bg: "#3498db", color: "#fff" }, // Azul
                         "Romance": { bg: "#e91e63", color: "#fff" }, // Rosa
                         "Aventura": { bg: "#27ae60", color: "#fff" }, // Verde
                         "Misteri": { bg: "#2c3e50", color: "#fff" }, // Azul oscuro
                     };
                     
                     return colorMap[genero] || { bg: "#95a5a6", color: "#fff" }; // Gris por defecto
                 };

                 const total = Number(serie.capitolsTemporada) || 0;
                 const currentCapitol = Number(serie.capitol) || 0;
                 const currentEpisode = currentCapitol % 100; // Extraer el número de episodio (últimos 2 dígitos)
                 const downloadedEpisodes = Math.max(0, currentEpisode - 1); // El episodio en BBDD es el siguiente a descargar
                 const progressPercent = total > 0 ? (downloadedEpisodes / total) * 100 : 0;
                 const episodesLeft = Math.max(0, total - downloadedEpisodes);

                return (
                <Col key={serie._id} xs={12} sm={6} lg={4} xl={3} className="mb-4">
                    <Card className={`series-grid-card h-100 ${!serie.activa ? 'inactive-serie' : ''}`}>
                        
                        {/* 1. Image Container */}
                        <div className="image-container">
                             <SeriesCardImage serie={serie} /> 
                        </div>

                        {/* 2. Top Badges (Floating) */}
                        <div className="status-badges">
                            <span 
                                className={`badge-dot ${serie.activa ? 'active' : 'inactive'}`} 
                                title={serie.activa ? 'Activa' : 'Inactiva / Pausada'}
                            ></span>
                        </div>
                        {serie.definicio && (
                            <div className="quality-badge">
                                <Badge pill>{serie.definicio.replace(/[\[\]]/g, '')}</Badge>
                            </div>
                        )}
                        {serie.activa && serie.genero && (
                            <div className="genre-badge">
                                <Badge 
                                    pill 
                                    className={`genre-${serie.genero.toLowerCase().replace(/[àèéíòóú]/g, (m) => ({à:'a',è:'e',é:'e',í:'i',ò:'o',ó:'o',ú:'u'}[m])).replace(/\s+/g, '-').replace(/ç/g, 'c')}`}
                                >
                                    {serie.genero}
                                </Badge>
                            </div>
                        )}

                        {/* 3. Bottom Info Content (Glassy) */}
                        <div className="serie-info-content">
                            <h5 className="serie-title" title={serie.nom}>{serie.nom}</h5>
                            
                            {serie.activa ? (
                                /* Información para series ACTIVAS */
                                <>
                                    <div className="serie-meta-v2">
                                        <div className="meta-pills-container">
                                            <span className="meta-pill season">T{serie.temporada}</span>
                                            <span className="meta-pill episode">Buscant Cap {(serie.capitol % 100)}</span>
                                            {episodesLeft > 0 && (
                                                <span className="meta-pill remaining">
                                                    Queden {episodesLeft}
                                                </span>
                                            )}
                                            {total > 0 && downloadedEpisodes >= total && (
                                                <span className="meta-pill completed">
                                                    <i className="bi bi-check-lg"></i>
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    
                                    {/* Mini Progress Bar - Solo para series activas */}
                                    {total > 0 && (
                                        <div className="progress-mini">
                                            <div className="progress-bar" style={{ width: `${Math.min(100, progressPercent)}%` }}></div>
                                        </div>
                                    )}
                                </>
                            ) : (
                                /* Información para series INACTIVAS */
                                <div className="serie-meta-v2">
                                    <div className="meta-pills-container">
                                        <span className="meta-pill season-inactive">T{serie.temporada}</span>
                                        {serie.genero && (
                                            <span className="meta-pill genre-simple">{serie.genero}</span>
                                        )}
                                    </div>
                                </div>
                            )}

                        </div>

                        {/* Renderizado condicional: Overlay para activas, Botones top-left para inactivas */}
                        {serie.activa ? (
                            /* 4. Hover Actions Overlay - Solo para series ACTIVAS */
                            <div className="serie-overlay-actions">
                                <div className="action-btn-group">
                                    <>
                                        <button 
                                            id={`btn-increment-${serie._id}`}
                                            className="btn-circle btn-success-premium" 
                                            onClick={() => onIncrement(serie)}
                                        >
                                            <i className="bi bi-play-circle-fill"></i>
                                        </button>
                                        <UncontrolledTooltip 
                                            placement="top" 
                                            target={`btn-increment-${serie._id}`}
                                        >
                                            Veure següent capítol (+1)
                                        </UncontrolledTooltip>
                                    </>
                                    
                                    {/* 
                                    <>
                                        <button 
                                            id={`btn-auto-download-${serie._id}`}
                                            className="btn-circle btn-download-auto" 
                                            onClick={() => onAutoDownload(serie)}
                                        >
                                            <i className="bi bi-cloud-download-fill"></i>
                                        </button>
                                        <UncontrolledTooltip 
                                            placement="top" 
                                            target={`btn-auto-download-${serie._id}`}
                                        >
                                            Buscar i descarregar automàticament
                                        </UncontrolledTooltip>
                                    </>
                                    */}
                                    
                                    <>
                                        <button 
                                            id={`btn-edit-${serie._id}`}
                                            className="btn-circle btn-primary-soft" 
                                            onClick={() => onEdit(serie)}
                                        >
                                            <i className="bi bi-gear-fill"></i>
                                        </button>
                                        <UncontrolledTooltip 
                                            placement="top" 
                                            target={`btn-edit-${serie._id}`}
                                        >
                                            Editar progrés i configuració
                                        </UncontrolledTooltip>
                                    </>
                                    
                                    <>
                                        <button 
                                            id={`btn-pause-${serie._id}`}
                                            className="btn-circle text-warning" 
                                            onClick={() => onDeactivate(serie._id)}
                                        >
                                            <i className="bi bi-archive-fill"></i>
                                        </button>
                                        <UncontrolledTooltip 
                                            placement="top" 
                                            target={`btn-pause-${serie._id}`}
                                        >
                                            Pausar i arxivar sèrie
                                        </UncontrolledTooltip>
                                    </>

                                    <>
                                        <button 
                                            id={`btn-delete-${serie._id}`}
                                            className="btn-circle btn-danger-soft" 
                                            onClick={() => onDelete(serie._id)}
                                        >
                                            <i className="bi bi-trash3-fill"></i>
                                        </button>
                                        <UncontrolledTooltip 
                                            placement="top" 
                                            target={`btn-delete-${serie._id}`}
                                        >
                                            Eliminar permanentment
                                        </UncontrolledTooltip>
                                    </>
                                </div>

                                <div className="d-flex gap-2 mt-2">
                                    <a 
                                        id={`btn-btdigg-${serie._id}`}
                                        href={`https://btdig.com/search?q=${encodeURIComponent(`${serie.nom || ''} ${serie.definicio || ''}`.trim())}&order=2`} 
                                        target="_blank" 
                                        rel="noopener noreferrer" 
                                        className="btn btn-sm btn-light rounded-pill px-3 fw-bold small"
                                    >
                                        <img src={btdiggIcon} alt="BT" height="14" className="me-2" style={{borderRadius:'50%'}}/> BTDigg
                                    </a>
                                    <UncontrolledTooltip 
                                        placement="bottom" 
                                        target={`btn-btdigg-${serie._id}`}
                                    >
                                        Buscar torrents en BTDigg
                                    </UncontrolledTooltip>
                                    
                                    <a 
                                        id={`btn-google-${serie._id}`}
                                        href={`https://www.google.com/search?q=${encodeURIComponent(serie.nom || '')} serie`} 
                                        target="_blank" 
                                        rel="noopener noreferrer" 
                                        className="btn btn-sm btn-light rounded-pill px-3 fw-bold small"
                                    >
                                        <img src={googleIcon} alt="G" height="14" className="me-2" style={{borderRadius:'50%'}}/> Google
                                    </a>
                                    <UncontrolledTooltip 
                                        placement="bottom" 
                                        target={`btn-google-${serie._id}`}
                                    >
                                        Buscar informació en Google
                                    </UncontrolledTooltip>
                                </div>
                            </div>
                        ) : (
                            /* Botones Top-Left - Solo para series INACTIVAS */
                            <div className="inactive-action-buttons">
                                <a
                                    id={`btn-inactive-btdigg-${serie._id}`}
                                    href={`https://btdig.com/search?q=${encodeURIComponent(`${serie.nom || ''} ${serie.definicio || ''}`.trim())}&order=2`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn-inactive-icon"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <i className="bi bi-magnet"></i>
                                </a>
                                <UncontrolledTooltip placement="top" target={`btn-inactive-btdigg-${serie._id}`}>
                                    Buscar torrents en BTDigg
                                </UncontrolledTooltip>

                                <a
                                    id={`btn-inactive-google-${serie._id}`}
                                    href={`https://www.google.com/search?q=${encodeURIComponent(serie.nom || '')} serie`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn-inactive-icon"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <i className="bi bi-search"></i>
                                </a>
                                <UncontrolledTooltip placement="top" target={`btn-inactive-google-${serie._id}`}>
                                    Buscar informació en Google
                                </UncontrolledTooltip>

                                <button
                                    id={`btn-inactive-edit-${serie._id}`}
                                    className="btn-inactive-icon"
                                    onClick={() => onEdit(serie)}
                                >
                                    <i className="bi bi-gear-fill"></i>
                                </button>
                                <UncontrolledTooltip placement="top" target={`btn-inactive-edit-${serie._id}`}>
                                    Editar progrés i configuració
                                </UncontrolledTooltip>

                                <button
                                    id={`btn-inactive-reactivate-${serie._id}`}
                                    className="btn-inactive-icon btn-inactive-success"
                                    onClick={() => onActivate(serie._id)}
                                >
                                    <i className="bi bi-arrow-counterclockwise"></i>
                                </button>
                                <UncontrolledTooltip placement="top" target={`btn-inactive-reactivate-${serie._id}`}>
                                    Reactivar sèrie
                                </UncontrolledTooltip>

                                <button
                                    id={`btn-inactive-delete-${serie._id}`}
                                    className="btn-inactive-icon btn-inactive-danger"
                                    onClick={() => onDelete(serie._id)}
                                >
                                    <i className="bi bi-trash3-fill"></i>
                                </button>
                                <UncontrolledTooltip placement="top" target={`btn-inactive-delete-${serie._id}`}>
                                    Eliminar permanentment
                                </UncontrolledTooltip>
                            </div>
                        )}


                    </Card>
                </Col>
            )})}
        </Row>
    );
};

export default SeriesGrid;
